// @flow

import React, { forwardRef, memo, lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { always, cond, T } from 'ramda'

import { PageLoaderInner } from '@alphaott/smart-tv-components/src/components/PageView/PageLoader'
import { getSmartTVVideoPlayer } from '@alphaott/app-main/selectors'
import { useDebugMode } from '@alphaott/smart-tv-debug/hooks'
import { P2PStatsOverlay } from '@alphaott/p2p'

import { PlayerContainer } from '../AlphaottVideoPlayer/AlphaottVideoPlayer.styles'

import { isShakaPlayer, isShakaPlayer3, isVideoJSPlayer } from './utils'
import { BaseControls } from '../AlphaottVideoPlayer/BaseControls'

import type { VideoPlayer as VideoPlayerTypes } from '../../types'

const ShakaPlayer = lazy(() =>
  import('@alphaott/smart-tv-shaka-player').then((module) => ({
    default: module.ShakaPlayerContainer,
  })),
)
const ShakaPlayer3 = lazy(() =>
  import('@alphaott/smart-tv-shaka-player').then((module) => ({
    default: module.ShakaPlayer3Container,
  })),
)
const VideoJSPlayer = lazy(() =>
  import('@alphaott/smart-tv-video-js-player').then((module) => ({
    default: module.VideoJSPlayerContainer,
  })),
)

type VideoPlayerProps = VideoPlayerTypes & {
  onRetry?: () => void,
  onDismiss?: () => void,
}

const VideoPlayerPure = (
  {
    videoId,
    mediaStreams,
    p2pConfig,
    autoplay,
    defaultTimePosition,
    onLoadMeta,
    onCanPlay,
    onLoading,
    onSeeking,
    onSeeked,
    onPlay,
    onPause,
    onEnd,
    onSourceLoaded,
    onTimeUpdate,
    onError,
    onChangeAudioTracks,
    onChangeSubtitleTracks,
    onDestroy,
    onRetry,
    onDismiss,
  }: VideoPlayerProps,
  playerRef: any,
) => {
  const { isDebugEnabled } = useDebugMode()

  const videoPlayer = useSelector(getSmartTVVideoPlayer)

  const Player = cond([
    // [isShakaPlayer3, always(ShakaPlayer3)],
    // [isShakaPlayer, always(ShakaPlayer)],
    // [isVideoJSPlayer, always(VideoJSPlayer)],
    [T, always(VideoJSPlayer)],
  ])(videoPlayer)

  return (
    <PlayerContainer>
      <Suspense fallback={<PageLoaderInner />}>
        <Player
          videoId={videoId}
          ref={playerRef}
          mediaStreams={mediaStreams}
          p2pConfig={p2pConfig}
          autoplay={autoplay}
          defaultTimePosition={defaultTimePosition}
          onLoadMeta={onLoadMeta}
          onCanPlay={onCanPlay}
          onLoading={onLoading}
          onSeeking={onSeeking}
          onSeeked={onSeeked}
          onPlay={onPlay}
          onPause={onPause}
          onEnd={onEnd}
          onSourceLoaded={onSourceLoaded}
          onTimeUpdate={onTimeUpdate}
          onError={onError}
          onChangeAudioTracks={onChangeAudioTracks}
          onChangeSubtitleTracks={onChangeSubtitleTracks}
          onDestroy={onDestroy}
        />
      </Suspense>
      <BaseControls onRetry={onRetry} onDismiss={onDismiss} />

      {isDebugEnabled && <P2PStatsOverlay scale={window.innerWidth / 1920} />}
    </PlayerContainer>
  )
}

export const VideoPlayer = memo<VideoPlayerProps>(forwardRef(VideoPlayerPure))

export default VideoPlayer
